import { Component, inject, DestroyRef, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { AppConfig } from '@app/core/app.config';
import { EventOriginEnum, NavigateToEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { ValidatorType } from '@app/core/enums/validator-type.enum';
import { Client } from '@app/core/model/client/client';
import { FieldValidator } from '@app/core/model/other/field-validator';
import { ClientsListService } from '@app/features/main/views/management/client/clients-inventory/clients-list.service';
import { TranslateService } from '@ngx-translate/core';
import ApiService from '@services/api.service';
import { AccessManager } from '@services/managers/access.manager';
import { AppManager } from '@services/managers/app.manager';
import { PopupManager } from '@services/managers/popup.manager';
import { SnackbarManager } from '@services/managers/snackbar.manager';
import { gql } from 'apollo-angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    templateUrl: './client-header.component.html',
    standalone: false
})
export class ClientHeaderComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly apiService = inject(ApiService);
  private readonly snackbarManager = inject(SnackbarManager);
  private readonly popupManager = inject(PopupManager);
  private readonly clientListService = inject(ClientsListService);
  private readonly appManager = inject(AppManager);
  protected readonly accessManager = inject(AccessManager);
  protected readonly appConfig = inject(AppConfig);
  private readonly translate = inject(TranslateService);
  private readonly analyticsService = inject(AnalyticsService);

  public client: Client;
  public Permission = PermissionEnum;

  public validators: FieldValidator[] = [];

  private destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.client = this.appManager.currentEntity as Client;
    this.validators = [
      new FieldValidator(ValidatorType.MAX_LENGTH, this.appConfig.NAME_MAX_LENGTH),
      new FieldValidator(ValidatorType.REQUIRED),
      new FieldValidator(ValidatorType.UNIQUE, this.client.name)
    ];

    this.clientListService.getDeleteClientsObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.router.navigate(['organization', this.appManager.currentOrganization.id, 'clients']).catch(console.error);
      });
  }

  /**
   * Updates the client name given the event sent by the inline widget
   * @param event
   */
  public saveClientName(event: { value: string }): void {
    const mutation = gql`
      mutation UpdateClientName($clientId: Int!, $clientInput: UpdateClientInput!) {
        updateClient(id: $clientId, client: $clientInput) {
          id
          name
        }
      }
    `;
    const variables = {
      clientId: this.client.id,
      clientInput: {
        name: event.value
      }
    };
    this.apiService.mutate({mutation, variables})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.EDIT_SAVED'));
        }
      });
  }

  /**
   * Deletes the client if all conditions are met
   * and redirects to the management list if successful
   */
  public deleteClient(): void {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.BUTTON,
      NavigateToEnum.EDIT_INLINE,
      EntityTypeEnum.CLIENT,
      this.client.id
    );
    if (this.client.isTb) {
      this.popupManager.showOkPopup({
        dialogTitle: this.translate.instant('TITLE.UNAUTHORIZED_DELETE'),
        dialogMessage: this.translate.instant('MESSAGE.UNAUTHORIZED_DELETE_TB_CLIENT'),
        type: 'error'
      });
      return;
    }
    this.clientListService.deleteClients([this.client]);
  }
}
